import { GrowthBookProvider } from '@growthbook/growthbook-react'
import { type ReactNode, useEffect, useState } from 'react'

import type { Environment } from '../../config'
import { useBookingStore } from '../../store'
import { APPLICATION_ID } from '../analytics'
import { getExperimentId, getGrowthBook, loadFeatures } from './growthbook'

interface FeatureFlagsProviderProps {
  env: Environment
  children: ReactNode
}

/**
 * Makes GrowthBook flags readable from any screen. Mounted by
 * `BookingToolRoot` around the whole flow, and deliberately not blocking on
 * the payload: only a screen a flag can step over waits for it, so a slow CDN
 * cannot hold up the booking flow.
 */
export const FeatureFlagsProvider = ({
  env,
  children,
}: FeatureFlagsProviderProps) => {
  const growthbook = getGrowthBook(env)
  const locationCode = useBookingStore((state) => state.locationCode)
  // `undefined` until `getExperimentId` answers; `null` is an answer (no
  // consent). Kept apart so an experiment hook can wait for the answer.
  const [id, setId] = useState<string | null>()

  useEffect(() => {
    void loadFeatures(env)
  }, [env])

  // Re-read on a consent change so a visitor who opts in mid-session joins
  // experiments, and one who opts out leaves them. Only the newest read is
  // applied: a slow consented read (up to 3 s for Segment) must not land after
  // a later opt-out and put the visitor back in an experiment.
  useEffect(() => {
    let latest = 0
    let current = true
    const refresh = () => {
      const request = ++latest
      void getExperimentId().then((next) => {
        if (current && request === latest) setId(next)
      })
    }
    refresh()
    window.addEventListener('OneTrustGroupsUpdated', refresh)
    return () => {
      current = false
      window.removeEventListener('OneTrustGroupsUpdated', refresh)
    }
  }, [])

  // Everything flag rules and experiments read, refreshed as the flow learns
  // it. This is the only writer, so the whole attribute set is written outright
  // — add a spread here if that ever stops being true (a plugin, a second
  // writer). `id` is left out until it is known: `'id' in getAttributes()` is
  // how a hook tells "not yet" from "no consent".
  useEffect(() => {
    void growthbook.setAttributes({
      ...(id !== undefined && { id }),
      applicationId: APPLICATION_ID,
      locationCode: locationCode ?? null,
    })
  }, [growthbook, id, locationCode])

  return (
    <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
  )
}
